import styled from "styled-components";
import { useFaceOnUploadModal } from "../contexts/ModalContext";
import { useDetailModal } from "../contexts/ModalContext";
import { useSquad } from "../contexts/SquadContext";
import Modal from "./Modal";
import CloseBg from "../assets/images/bg_close.svg";
import PlayerCard from "./PlayerCard";
import { useState, useRef, useEffect } from "react";
import { uploadPlayerImage } from "../api/uploadPlayerImage";

// PlayerCard 비율: 6.25rem : 9.0625rem = 약 1 : 1.45
const CROP_RATIO = 9.0625 / 6.25; // 세로/가로 = 약 1.45

const ModalContainer = styled.div`
    width: 80rem;
    height: 50rem;
    background-color: white;
    display: flex;
    flex-direction: column;
`;

const ModalHeader = styled.div`
    position: relative;
    height: 5.75rem;
    justify-content: center;
    align-items: center;
    display: flex;
    font-family: 'Esamanru', sans-serif;
    font-size: 1.5rem;
    font-weight: 200;
    color: #545454;
    border-bottom: 0.125rem solid #545454;
`;

const CloseButton = styled.img`
    position: absolute;
    right: 1.625rem;
    width: 2.5rem;
    height: 2.5rem;
    cursor: pointer;
`;

const ModalContent = styled.div`
    display: flex;
    flex-direction: row;
    flex: 1;
    overflow: hidden;
`;

const UploadSection = styled.div`
    flex: 1;
    padding: 2rem;
    border-right: 0.125rem solid #e0e0e0;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
`;

const PreviewSection = styled.div`
    flex: 1;
    padding: 2rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    overflow-y: auto;
`;

const UploadLabel = styled.label`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 15rem;
    border: 2px dashed #545454;
    border-radius: 0.5rem;
    background-color: #f9f9f9;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background-color: #f0f0f0;
        border-color: #000;
    }

    &.drag-over {
        background-color: #e8f5e9;
        border-color: #4caf50;
    }
`;

const HiddenInput = styled.input`
    display: none;
`;

const UploadText = styled.div`
    font-family: 'Pretendard Variable';
    font-size: 1.25rem;
    font-weight: 600;
    color: #545454;
    text-align: center;
    margin-bottom: 0.5rem;
`;

const UploadSubText = styled.div`
    font-family: 'Pretendard Variable';
    font-size: 0.875rem;
    color: #999;
`;

const SelectedFileName = styled.div`
    font-family: 'Pretendard Variable';
    font-size: 1rem;
    color: #545454;
    margin-top: 1rem;
`;

const CropContainer = styled.div`
    margin-top: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
`;

const CropTitle = styled.div`
    font-family: 'Pretendard Variable';
    font-size: 1rem;
    font-weight: 600;
    color: #545454;
`;

const CropCanvasWrapper = styled.div`
    position: relative;
    width: 100%;
    display: flex;
    justify-content: center;
    margin-bottom: 1rem;
    border: 1px solid #ddd;
    border-radius: 0.375rem;
    background-color: transparent;
    padding: 1rem;
    box-sizing: border-box;
`;

const CropFrame = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: ${1 / CROP_RATIO};
    max-width: 15rem;
    border: 3px solid #545454;
    border-radius: 0.375rem;
    background-color: transparent;
    overflow: hidden;
    cursor: grab;

    &:active {
        cursor: grabbing;
    }
`;

const CropImage = styled.img`
    position: absolute;
    width: ${props => props.$imageWidth ? Math.round(props.$imageWidth * props.$scale) : 'auto'}px;
    height: auto;
    top: ${props => props.$offsetY}px;
    left: ${props => props.$offsetX}px;
    cursor: grab;
    user-select: none;
    pointer-events: none;
`;

const ZoomControls = styled.div`
    display: flex;
    gap: 0.5rem;
    align-items: center;
    margin-top: 1rem;
`;

const ZoomLabel = styled.div`
    font-family: 'Pretendard Variable';
    font-size: 0.875rem;
    color: #545454;
    min-width: 2rem;
`;

const ZoomInput = styled.input`
    width: 4rem;
    padding: 0.25rem 0.5rem;
    font-family: 'Pretendard Variable';
    font-size: 0.875rem;
    border: 1px solid #999;
    border-radius: 0.25rem;
    text-align: center;

    &:focus {
        outline: none;
        border-color: #545454;
    }
`;

const ZoomSlider = styled.input`
    flex: 1;
    height: 0.375rem;
    border-radius: 0.25rem;
    background: #ddd;
    outline: none;
    -webkit-appearance: none;

    &::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        width: 1rem;
        height: 1rem;
        border-radius: 50%;
        background: #545454;
        cursor: pointer;
    }

    &::-moz-range-thumb {
        width: 1rem;
        height: 1rem;
        border-radius: 50%;
        background: #545454;
        cursor: pointer;
        border: none;
    }
`;

const ResetButton = styled.button`
    padding: 0.5rem 1rem;
    font-family: 'Pretendard Variable';
    font-size: 0.875rem;
    font-weight: 600;
    border: 1px solid #999;
    background-color: white;
    color: #545454;
    border-radius: 0.375rem;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background-color: #f0f0f0;
    }
`;

const CropControls = styled.div`
    display: flex;
    gap: 0.5rem;
`;

const SmallButton = styled.button`
    flex: 1;
    padding: 0.5rem 1rem;
    font-family: 'Pretendard Variable';
    font-size: 0.875rem;
    font-weight: 600;
    border: 1px solid #999;
    background-color: white;
    color: #545454;
    border-radius: 0.375rem;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
        background-color: #f0f0f0;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const ModalFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    padding: 1.5rem 2rem;
    border-top: 0.125rem solid #e0e0e0;
    background-color: #f9f9f9;
`;

const Button = styled.button`
    padding: 0.75rem 1.5rem;
    font-family: 'Pretendard Variable';
    font-size: 1rem;
    font-weight: 600;
    border: none;
    border-radius: 0.375rem;
    cursor: pointer;
    transition: all 0.3s ease;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const CancelButton = styled(Button)`
    background-color: #e0e0e0;
    color: #545454;

    &:hover:not(:disabled) {
        background-color: #d0d0d0;
    }
`;

const UploadButton = styled(Button)`
    background-color: #545454;
    color: white;

    &:hover:not(:disabled) {
        background-color: #333;
    }
`;

const LoadingText = styled.div`
    font-family: 'Pretendard Variable';
    font-size: 1rem;
    color: #999;
`;

export default function FaceOnUploadModal() {
    const { isFaceOnUploadModalOpen, closeFaceOnUploadModal } = useFaceOnUploadModal();
    const { isDetailModalOpen } = useDetailModal();
    const { playerInfos } = useSquad();

    const [selectedFile, setSelectedFile] = useState(null);
    const [originalImageUrl, setOriginalImageUrl] = useState(null);
    const [croppedImageUrl, setCroppedImageUrl] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);

    // 이미지 crop 조절용
    const [offsetX, setOffsetX] = useState(0);
    const [offsetY, setOffsetY] = useState(0);
    const [scale, setScale] = useState(1);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [imageWidth, setImageWidth] = useState(0);

    const imageRef = useRef(null);
    const cropFrameRef = useRef(null);
    const canvasRef = useRef(null);

    const currentPlayer = playerInfos[isDetailModalOpen];

    // scale, offset 변경 시 이미지 재생성
    useEffect(() => {
        if (originalImageUrl && imageRef.current) {
            generateCroppedImage();
        }
    }, [scale, offsetX, offsetY]);

    // URL.createObjectURL 메모리 누수 방지
    useEffect(() => {
        return () => {
            if (croppedImageUrl) {
                URL.revokeObjectURL(croppedImageUrl);
            }
            if (originalImageUrl && originalImageUrl.startsWith('blob:')) {
                URL.revokeObjectURL(originalImageUrl);
            }
        };
    }, [croppedImageUrl, originalImageUrl]);

    const handleFileSelect = (file) => {
        if (!file) return;

        // 파일 검증
        if (!file.type.startsWith('image/')) {
            alert('이미지 파일만 선택 가능합니다.');
            return;
        }

        if (file.size === 0) {
            alert('0바이트 파일은 업로드할 수 없습니다.');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            alert('10MB 이하의 파일만 업로드 가능합니다.');
            return;
        }

        setSelectedFile(file);
        setCroppedImageUrl(null);

        // 원본 이미지 미리보기
        const reader = new FileReader();
        reader.onload = (e) => {
            setOriginalImageUrl(e.target.result);
        };
        reader.readAsDataURL(file);
    };

    const handleInputChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);

        const file = e.dataTransfer.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    // 드래그 시작
    const handleMouseDown = (e) => {
        setIsDragging(true);
        setDragStart({ x: e.clientX, y: e.clientY });
    };

    // 드래그 중
    const handleMouseMove = (e) => {
        if (!isDragging) return;

        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y;

        setOffsetX(prev => prev + deltaX);
        setOffsetY(prev => prev + deltaY);
        setDragStart({ x: e.clientX, y: e.clientY });
    };

    // 드래그 종료
    const handleMouseUp = () => {
        setIsDragging(false);
    };

    // 휠로 확대/축소
    const handleWheel = (e) => {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setScale(prev => Math.max(0.1, Math.min(3, prev + delta)));
    };

    // 초기화
    const handleReset = () => {
        setOffsetX(0);
        setOffsetY(0);
        setScale(1);
    };

    // 이미지 로드 시 초기화
    const handleImageLoad = (event) => {
        setImageWidth(event.target.naturalWidth);
        handleReset();
        generateCroppedImage();
    };

    // 크롭된 이미지 생성
    const generateCroppedImage = () => {
        if (!imageRef.current || !cropFrameRef.current || !canvasRef.current) return;

        const img = imageRef.current;
        const frame = cropFrameRef.current;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d', { alpha: true });

        const frameWidth = frame.clientWidth;
        const frameHeight = frame.clientHeight;
        const scaledImgWidth = img.naturalWidth * scale;
        const scaledImgHeight = img.naturalHeight * scale;

        canvas.width = frameWidth;
        canvas.height = frameHeight;

        // 프레임 내에서 이미지의 위치
        const drawX = offsetX;
        const drawY = offsetY;

        // 투명 배경 유지 (배경을 채우지 않음)
        // ctx.fillStyle = '#000';
        // ctx.fillRect(0, 0, frameWidth, frameHeight);

        // 이미지를 canvas에 그리기
        ctx.drawImage(
            img,
            0, 0,
            img.naturalWidth, img.naturalHeight,
            drawX, drawY,
            scaledImgWidth, scaledImgHeight
        );

        canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            setCroppedImageUrl(url);
            setSelectedFile(blob);
        }, 'image/png');
    };

    // scale이나 offset 변경 시 이미지 재생성
    const updateCrop = () => {
        generateCroppedImage();
    };

    // 업로드용 이미지 리사이징 (300x435px로 고정, WebP 변환)
    const resizeImageForUpload = () => {
        return new Promise((resolve, reject) => {
            if (!imageRef.current || !cropFrameRef.current || !canvasRef.current) {
                reject(new Error('이미지 정보가 없습니다.'));
                return;
            }

            const img = imageRef.current;
            const frame = cropFrameRef.current;
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d', { alpha: true });

            const UPLOAD_WIDTH = 300;
            const UPLOAD_HEIGHT = 435;
            const UPLOAD_QUALITY = 0.9;

            // 최종 업로드 크기로 canvas 설정
            canvas.width = UPLOAD_WIDTH;
            canvas.height = UPLOAD_HEIGHT;

            // 프레임에서의 스케일 계산
            const frameWidth = frame.clientWidth;
            const frameHeight = frame.clientHeight;
            const scaleX = UPLOAD_WIDTH / frameWidth;
            const scaleY = UPLOAD_HEIGHT / frameHeight;

            // 스케일된 이미지 크기와 위치 계산
            const scaledImgWidth = img.naturalWidth * scale;
            const scaledImgHeight = img.naturalHeight * scale;
            const scaledOffsetX = offsetX * scaleX;
            const scaledOffsetY = offsetY * scaleY;

            // 업로드 크기로 이미지 그리기
            ctx.drawImage(
                img,
                0, 0,
                img.naturalWidth, img.naturalHeight,
                scaledOffsetX, scaledOffsetY,
                scaledImgWidth * scaleX, scaledImgHeight * scaleY
            );

            // WebP로 변환 (Safari 호환성을 위해 실패 시 PNG 대체)
            canvas.toBlob(
                (blob) => {
                    resolve(blob);
                },
                'image/webp',
                UPLOAD_QUALITY
            );
        });
    };

    const handleUpload = async () => {
        if (!selectedFile || !currentPlayer) {
            alert('파일과 선수 정보가 필요합니다.');
            return;
        }

        setIsUploading(true);

        try {
            // 업로드용 이미지 리사이징 (300x435px, WebP)
            const resizedBlob = await resizeImageForUpload();

            // File 객체로 변환 (uploadPlayerImage가 File 객체를 기대함)
            const resizedFile = new File([resizedBlob], 'cropped-image.webp', { type: resizedBlob.type });

            await uploadPlayerImage(currentPlayer.playerName, currentPlayer.team, resizedFile);
            alert('이미지가 성공적으로 업로드되었습니다.');
            handleClose();
        } catch (error) {
            console.error('이미지 업로드 실패:', error);
            alert('이미지 업로드에 실패했습니다. 다시 시도해주세요.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        setOriginalImageUrl(null);
        setCroppedImageUrl(null);
        closeFaceOnUploadModal();
    };

    if (!currentPlayer) {
        return null;
    }

    return (
        <Modal
            isOpen={isFaceOnUploadModalOpen}
            onClose={handleClose}
        >
            <ModalContainer>
                <ModalHeader>
                    선수 사진 추가 - {currentPlayer.type === '임팩트' ? currentPlayer.description + " " : currentPlayer.type} {currentPlayer.playerName}{currentPlayer.year != null ? ("'" + currentPlayer.year) : ""}
                    <CloseButton
                        src={CloseBg}
                        onClick={handleClose}
                    />
                </ModalHeader>

                <ModalContent>
                    <UploadSection>
                        {!originalImageUrl ? (
                            <>
                                <UploadLabel
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onDrop={handleDrop}
                                    className={isDragOver ? 'drag-over' : ''}
                                >
                                    <UploadText>이미지를 여기에 드래그하거나 클릭해주세요</UploadText>
                                    <UploadSubText>지원 형식: JPG, PNG, WebP (최대 10MB)</UploadSubText>
                                    <HiddenInput
                                        type="file"
                                        accept="image/*"
                                        onChange={handleInputChange}
                                    />
                                </UploadLabel>

                                {selectedFile && (
                                    <SelectedFileName>
                                        ✓ {selectedFile.name}
                                    </SelectedFileName>
                                )}
                            </>
                        ) : (
                            <CropContainer>
                                <CropTitle>이미지 자르기 및 조정</CropTitle>

                                <CropFrame
                                    ref={cropFrameRef}
                                    onMouseDown={handleMouseDown}
                                    onMouseMove={handleMouseMove}
                                    onMouseUp={handleMouseUp}
                                    onMouseLeave={handleMouseUp}
                                    onWheel={handleWheel}
                                >
                                    <CropImage
                                        ref={imageRef}
                                        src={originalImageUrl}
                                        alt="crop-source"
                                        $offsetX={offsetX}
                                        $offsetY={offsetY}
                                        $scale={scale}
                                        $imageWidth={imageWidth}
                                        onLoad={handleImageLoad}
                                        onDragStart={(e) => e.preventDefault()}
                                    />
                                </CropFrame>

                                <canvas
                                    ref={canvasRef}
                                    style={{ display: 'none' }}
                                />

                                <ZoomControls>
                                    <ZoomLabel>줌:</ZoomLabel>
                                    <ZoomSlider
                                        type="range"
                                        min="0.1"
                                        max="2"
                                        step="0.01"
                                        value={scale}
                                        onChange={(event) => {
                                            setScale(parseFloat(event.target.value));
                                        }}
                                    />
                                    <ZoomInput
                                        type="number"
                                        min="0.1"
                                        max="2"
                                        step="0.01"
                                        value={scale}
                                        onChange={(event) => {
                                            const value = parseFloat(event.target.value);
                                            if (!isNaN(value)) {
                                                setScale(Math.max(0.1, Math.min(2, value)));
                                            }
                                        }}
                                    />
                                </ZoomControls>

                                <CropControls style={{ marginTop: '1rem' }}>
                                    <SmallButton onClick={() => setOriginalImageUrl(null)}>
                                        다시 선택
                                    </SmallButton>
                                    <ResetButton onClick={handleReset}>
                                        초기화
                                    </ResetButton>
                                </CropControls>
                            </CropContainer>
                        )}
                    </UploadSection>

                    <PreviewSection>
                        {croppedImageUrl ? (
                            <PlayerCard
                                player={currentPlayer}
                                isForDisplay={true}
                                initialFaceOn={croppedImageUrl}
                                scale={2.5}
                            />
                        ) : (
                            <LoadingText>이미지를 선택하면 미리보기가 표시됩니다</LoadingText>
                        )}
                    </PreviewSection>
                </ModalContent>

                <ModalFooter>
                    <CancelButton onClick={handleClose}>
                        취소
                    </CancelButton>
                    <UploadButton
                        onClick={handleUpload}
                        disabled={!croppedImageUrl || isUploading}
                    >
                        {isUploading ? '업로드 중...' : '업로드 완료'}
                    </UploadButton>
                </ModalFooter>
            </ModalContainer>
        </Modal>
    );
}
